import { Component, Input, OnChanges, OnInit, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray, transferArrayItem, DragDropModule } from '@angular/cdk/drag-drop';
import { WebsiteService, PageWithChildren, Page, SupportedLanguage } from '../../services/website.service';
import { PostService, Post } from '../../services/post.service';
import { ProjectService, Project } from '../../services/project.service';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from '../../services/notification.service';
import { FormsModule } from '@angular/forms';
import { MediaSelectorModalComponent } from '../../pages/pages/page-editor/components/media-selector-modal/media-selector-modal.component';

@Component({
  selector: 'app-menu-editor',
  standalone: true,
  imports: [CommonModule, DragDropModule, FormsModule, MediaSelectorModalComponent],
  templateUrl: './menu-editor.component.html',
  styleUrls: ['./menu-editor.component.scss']
})
export class MenuEditorComponent implements OnInit, OnChanges {
  @Input() domain!: string;
  @Input() currentLocale: SupportedLanguage = 'en';
  @Output() menuChanged = new EventEmitter<void>();
  
  rootPages: PageWithChildren[] = [];
  expandedPages = new Set<string>();
  expandedSettingsPages = new Set<string>();
  showAddToMenuForm = false;
  showAddUrlForm = false;
  selectedPageId: string = '';
  availablePages: Page[] = [];

  // External URL form
  newLinkTitle = '';
  newLinkUrl = '';
  newLinkOpenInNewTab = true;

  // Post als menu-item
  showAddPostForm = false;
  availablePosts: Post[] = [];
  postSearchTerm = '';

  // Project als menu-item
  showAddProjectForm = false;
  availableProjects: Project[] = [];
  projectSearchTerm = '';

  get filteredPosts(): Post[] {
    if (!this.postSearchTerm.trim()) return this.availablePosts;
    const term = this.postSearchTerm.toLowerCase();
    return this.availablePosts.filter(p =>
      p.title?.toLowerCase().includes(term) || p.slug?.toLowerCase().includes(term)
    );
  }

  get filteredProjects(): Project[] {
    if (!this.projectSearchTerm.trim()) return this.availableProjects;
    const term = this.projectSearchTerm.toLowerCase();
    return this.availableProjects.filter(p =>
      p.title?.toLowerCase().includes(term) ||
      p.title_nl?.toLowerCase().includes(term) ||
      p.slug?.toLowerCase().includes(term)
    );
  }

  // Media selector for advertising highlight image
  showMediaSelectorModal = false;
  mediaSelectorTargetPage: PageWithChildren | null = null;

  constructor(
    private websiteService: WebsiteService,
    private postService: PostService,
    private projectService: ProjectService,
    private notificationService: NotificationService,
    private router: Router
  ) {}

  editMenuItem(page: PageWithChildren) {
    // Post-based menu items have route starting with 'post-'
    if (page.isExternalLink && page.route?.startsWith('post-')) {
      // Navigate to post editor — find the post by slug
      const slug = page.route.replace('post-', '');
      this.postService.getPostBySlug(this.domain, this.currentLocale, slug).then(post => {
        if (post) {
          this.router.navigate(['/posts/edit', post.id]);
        } else {
          this.notificationService.show({ message: 'Post niet gevonden', type: 'error' });
        }
      });
    } else if (page.isExternalLink && page.route?.startsWith('project-')) {
      // Navigate to project editor
      const slug = page.route.replace('project-', '');
      firstValueFrom(this.projectService.getProjects()).then(projects => {
        const project = projects.find(p => p.slug === slug);
        if (project?.id) {
          this.router.navigate(['/projects', project.id]);
        } else {
          this.notificationService.show({ message: 'Project niet gevonden', type: 'error' });
        }
      });
    } else if (!page.isExternalLink) {
      // Regular page — navigate to page editor
      this.router.navigate(['/pages', page.id]);
    }
  }

  async ngOnInit() {
    if (this.domain) {
      await this.loadPages();
      await this.loadAvailablePages();
    }
  }

  private async getPostUrlPrefix(post: Post): Promise<string> {
    if (!post.categoryIds?.length || !this.domain) return 'posts';
    const categories = await this.websiteService.getCategories(this.domain);
    const firstCat = categories.find(c => c.id === post.categoryIds![0]);
    if (!firstCat) return 'posts';
    return firstCat.slug || firstCat.name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
  }

  async ngOnChanges() {
    if (this.domain) {
      await this.loadMenuStructure();
      await this.loadAvailablePages();
    }
    if (this.currentLocale) {
      await this.loadMenuStructure();
      await this.loadAvailablePages(); // Reload pages when language changes
    }
  }

  async loadPages() {
    this.rootPages = await this.websiteService.getMenuStructure(this.domain, this.currentLocale);
  }

  async loadMenuStructure() {
    try {
      this.rootPages = await this.websiteService.getMenuStructure(this.domain, this.currentLocale);
    } catch (error) {
      console.error('Error loading menu structure:', error);
      this.notificationService.show({
        message: 'Failed to load menu structure',
        type: 'error'
      });
    }
  }

  async loadAvailablePages() {
    try {
      // Load all pages for the domain and current language
      this.availablePages = await this.websiteService.getPagesByDomain(this.domain, this.currentLocale);
    } catch (error) {
      console.error('Error loading available pages:', error);
    }
  }

  toggleChildren(page: PageWithChildren) {
    if (this.expandedPages.has(page.id)) {
      this.expandedPages.delete(page.id);
    } else {
      this.expandedPages.add(page.id);
    }
  }

  async drop(event: CdkDragDrop<any>) {
    if (!event.container.data) return;
    
    const container = event.container.data as PageWithChildren[];
    const previousContainer = event.previousContainer.data as PageWithChildren[];
    
    if (event.previousContainer === event.container) {
      // Same container - just reordering
      moveItemInArray(container, event.previousIndex, event.currentIndex);
    } else {
      // Moving between containers
      transferArrayItem(
        previousContainer,
        container,
        event.previousIndex,
        event.currentIndex
      );
    }

    // Update all pages with their new order and parentId
    await this.updatePagesFromTree();

    this.notificationService.show({
      message: 'Menu structuur bijgewerkt',
      type: 'success'
    });
    this.menuChanged.emit();
  }

  private async updatePagesFromTree() {
    // Flatten the tree and update each page's order and parentId
    const updatePages = async (pages: PageWithChildren[], parentId: string | null = null, startOrder: number = 0): Promise<number> => {
      let currentOrder = startOrder;
      for (const page of pages) {
        // Update this page's order and parentId
        await this.websiteService.updatePage({
          ...page,
          order: currentOrder,
          parentId: parentId,
          updatedAt: new Date()
        });
        currentOrder++;
        
        // Update children recursively
        if (page.children && page.children.length > 0) {
          currentOrder = await updatePages(page.children, page.id, currentOrder);
        }
      }
      return currentOrder;
    };
    
    await updatePages(this.rootPages);
    // Reload the menu structure to reflect changes
    await this.loadMenuStructure();
  }

  // Add this helper method to find the parent page
  private findParentPage(pages: PageWithChildren[], targetContainer: PageWithChildren[]): PageWithChildren | null {
    for (const page of pages) {
      if (page.children === targetContainer) {
        return page;
      }
      if (page.children) {
        const found = this.findParentPage(page.children, targetContainer);
        if (found) return found;
      }
    }
    return null;
  }


  async addToMenu() {
    if (!this.selectedPageId) return;

    try {
      const page = await this.websiteService.getPageById(this.selectedPageId);
      if (!page) {
        throw new Error('Page not found');
      }

      // Calculate the highest order in the current menu
      const allPages = this.getAllPagesFromTree(this.rootPages);
      const maxOrder = allPages.length > 0 
        ? Math.max(...allPages.map(p => p.order || 0))
        : -1;
      const newOrder = maxOrder + 1;

      // Update the page to add it to the menu
      await this.websiteService.updatePage({
        ...page,
        order: newOrder,
        parentId: null,
        published: true,
        updatedAt: new Date()
      });

      this.selectedPageId = '';
      this.showAddToMenuForm = false;
      await this.loadMenuStructure();
      await this.loadAvailablePages();
      this.notificationService.show({
        message: 'Pagina toegevoegd aan menu',
        type: 'success'
      });
    } catch (error) {
      console.error('Error adding page to menu:', error);
      this.notificationService.show({
        message: 'Pagina toevoegen aan menu mislukt',
        type: 'error'
      });
    }
  }

  private getAllPagesFromTree(pages: PageWithChildren[]): PageWithChildren[] {
    const result: PageWithChildren[] = [];
    const collect = (pages: PageWithChildren[]) => {
      pages.forEach(page => {
        result.push(page);
        if (page.children && page.children.length > 0) {
          collect(page.children);
        }
      });
    };
    collect(pages);
    return result;
  }

  async removeFromMenu(page: PageWithChildren) {
    try {
      // Update the page to remove it from menu (set order to -1)
      await this.websiteService.updatePage({
        ...page,
        order: -1,
        parentId: null,
        updatedAt: new Date()
      });
      
      // Also update all children to remove them from menu
      const removeChildren = async (children: PageWithChildren[]) => {
        for (const child of children) {
          await this.websiteService.updatePage({
            ...child,
            order: -1,
            parentId: null,
            updatedAt: new Date()
          });
          if (child.children && child.children.length > 0) {
            await removeChildren(child.children);
          }
        }
      };
      
      if (page.children && page.children.length > 0) {
        await removeChildren(page.children);
      }
      
      await this.loadMenuStructure();
      this.notificationService.show({
        message: 'Pagina verwijderd uit menu',
        type: 'success'
      });
    } catch (error) {
      console.error('Error removing page from menu:', error);
      this.notificationService.show({
        message: 'Pagina verwijderen uit menu mislukt',
        type: 'error'
      });
    }
  }

  async toggleNesting(page: PageWithChildren) {
    const currentParentId = page.parentId;
    const newParentId = currentParentId ? null : this.findPreviousPage(page)?.id;
    
    await this.websiteService.updatePageOrder(page.id, page.order || 0, newParentId);
    await this.refreshStructure();
  }

  public findPreviousPage(currentPage: PageWithChildren): PageWithChildren | null {
    const index = this.rootPages.findIndex(p => p.id === currentPage.id);
    return index > 0 ? this.rootPages[index - 1] : null;
  }

  async makeRoot(page: PageWithChildren) {
    try {
      // Calculate the highest order in root pages
      const maxOrder = this.rootPages.length > 0 
        ? Math.max(...this.rootPages.map(p => p.order || 0))
        : -1;
      const newOrder = maxOrder + 1;

      // Update the page to be a root page
      await this.websiteService.updatePage({
        ...page,
        order: newOrder,
        parentId: null,
        updatedAt: new Date()
      });
      
      await this.loadMenuStructure();
      this.notificationService.show({
        message: 'Pagina verplaatst naar hoofdniveau',
        type: 'success'
      });
    } catch (error) {
      console.error('Error making page root:', error);
      this.notificationService.show({
        message: 'Pagina verplaatsen mislukt',
        type: 'error'
      });
    }
  }

  async makeChild(page: PageWithChildren) {
    try {
      const previousPage = this.findPreviousPage(page);
      if (!previousPage) return;
      
      // Calculate the order for the child (after all existing children of previousPage)
      const existingChildren = previousPage.children || [];
      const maxChildOrder = existingChildren.length > 0
        ? Math.max(...existingChildren.map(c => c.order || 0))
        : (previousPage.order || 0);
      const newOrder = maxChildOrder + 1;

      // Update the page to be a child of previousPage
      await this.websiteService.updatePage({
        ...page,
        order: newOrder,
        parentId: previousPage.id,
        updatedAt: new Date()
      });
      
      await this.loadMenuStructure();
      this.notificationService.show({
        message: 'Pagina verplaatst als onderliggend item',
        type: 'success'
      });
    } catch (error) {
      console.error('Error making page child:', error);
      this.notificationService.show({
        message: 'Pagina verplaatsen mislukt',
        type: 'error'
      });
    }
  }

  private async refreshStructure() {
    await this.loadMenuStructure();
    this.menuChanged.emit();
  }

  async openAddToMenuForm() {
    // Reload available pages and menu structure to ensure we have the latest data
    await this.loadAvailablePages();
    await this.loadMenuStructure();
    this.showAddToMenuForm = true;
  }

  getUnassignedPages(): Page[] {
    if (!this.availablePages || this.availablePages.length === 0) {
      return [];
    }
    
    // Get all page IDs that are currently in the menu structure (order >= 0)
    const menuPageIds = new Set(
      this.getAllPagesFromTree(this.rootPages).map(page => page.id)
    );
    
    // Return pages that are not in the menu and have order < 0 (not yet in menu)
    return this.availablePages.filter(page => 
      !menuPageIds.has(page.id) && 
      (page.order === undefined || page.order < 0)
    );
  }

  async addUnassignedPageToMenu(page: Page) {
    try {
      // Get the full page data to update
      const fullPage = await this.websiteService.getPageById(page.id);
      if (!fullPage) {
        throw new Error('Page not found');
      }

      // Calculate the highest order in the current menu
      const allPages = this.getAllPagesFromTree(this.rootPages);
      const maxOrder = allPages.length > 0 
        ? Math.max(...allPages.map(p => p.order || 0))
        : -1;
      const newOrder = maxOrder + 1;

      // Update the page: set order >= 0 (in menu) and publish it
      await this.websiteService.updatePage({
        ...fullPage,
        order: newOrder,
        parentId: null,
        published: true, // Auto-publish when adding to menu
        updatedAt: new Date()
      });

      // Reload menu structure to show the new page
      await this.loadMenuStructure();
      await this.loadAvailablePages();
      
      // Always close the modal after adding
      this.showAddToMenuForm = false;
      
      this.notificationService.show({
        message: 'Pagina toegevoegd aan menu',
        type: 'success'
      });
    } catch (error) {
      console.error('Error adding page to menu:', error);
      this.notificationService.show({
        message: 'Pagina toevoegen aan menu mislukt',
        type: 'error'
      });
    }
  }

  async togglePublished(page: PageWithChildren) {
    try {
      const newPublishedStatus = page.published !== false ? false : true;

      // Get the full page data to update
      const fullPage = await this.websiteService.getPageById(page.id);
      if (!fullPage) {
        throw new Error('Page not found');
      }

      // Update the page with new published status
      await this.websiteService.updatePage({
        ...fullPage,
        published: newPublishedStatus,
        updatedAt: new Date()
      });

      // If unpublishing, also unpublish all children recursively
      if (newPublishedStatus === false && page.children && page.children.length > 0) {
        await this.unpublishChildren(page.children);
      }

      // Reload menu structure to reflect changes
      await this.loadMenuStructure();
      
      this.notificationService.show({
        message: newPublishedStatus ? 'Pagina gepubliceerd' : 'Pagina gedepubliceerd',
        type: 'success'
      });
    } catch (error) {
      console.error('Error toggling published status:', error);
      this.notificationService.show({
        message: 'Status bijwerken mislukt',
        type: 'error'
      });
    }
  }

  private async unpublishChildren(children: PageWithChildren[]): Promise<void> {
    for (const child of children) {
      const fullChild = await this.websiteService.getPageById(child.id);
      if (fullChild) {
        await this.websiteService.updatePage({
          ...fullChild,
          published: false,
          updatedAt: new Date()
        });

        // Recursively unpublish grandchildren
        if (child.children && child.children.length > 0) {
          await this.unpublishChildren(child.children);
        }
      }
    }
  }

  toggleSettings(page: PageWithChildren) {
    if (this.expandedSettingsPages.has(page.id)) {
      this.expandedSettingsPages.delete(page.id);
    } else {
      this.expandedSettingsPages.add(page.id);
    }
  }

  async addExternalLink() {
    if (!this.newLinkTitle || !this.newLinkUrl) return;

    try {
      // Calculate the highest order in the current menu
      const allPages = this.getAllPagesFromTree(this.rootPages);
      const maxOrder = allPages.length > 0
        ? Math.max(...allPages.map(p => p.order || 0))
        : -1;
      const newOrder = maxOrder + 1;

      // Create the page first with standard fields
      const pageId = await this.websiteService.createPage(
        this.domain,
        this.newLinkTitle,
        'external-' + Date.now(),
        this.currentLocale,
        newOrder
      );

      // Then update it with external link fields + published
      const createdPage = await this.websiteService.getPageById(pageId);
      if (createdPage) {
        await this.websiteService.updatePage({
          ...createdPage,
          published: true,
          isExternalLink: true,
          externalUrl: this.newLinkUrl,
          openInNewTab: this.newLinkOpenInNewTab,
          updatedAt: new Date()
        });
      }

      // Reset form
      this.newLinkTitle = '';
      this.newLinkUrl = '';
      this.newLinkOpenInNewTab = true;
      this.showAddUrlForm = false;

      await this.loadMenuStructure();
      this.notificationService.show({
        message: 'URL toegevoegd aan menu',
        type: 'success'
      });
      this.menuChanged.emit();
    } catch (error) {
      console.error('Error adding external link:', error);
      this.notificationService.show({
        message: 'URL toevoegen mislukt',
        type: 'error'
      });
    }
  }

  async loadAvailablePosts() {
    try {
      const posts = await this.postService.getPosts(this.domain, this.currentLocale);
      this.availablePosts = posts.filter(p => p.status === 'published' && p.slug);
    } catch (error) {
      console.error('Error loading posts:', error);
      this.availablePosts = [];
    }
  }

  async addPostToMenu(post: Post) {
    if (!post.slug) return;

    try {
      const allPages = this.getAllPagesFromTree(this.rootPages);
      const maxOrder = allPages.length > 0
        ? Math.max(...allPages.map(p => p.order || 0))
        : -1;
      const newOrder = maxOrder + 1;

      // Maak een pagina aan die linkt naar de post
      const pageId = await this.websiteService.createPage(
        this.domain,
        post.title,
        'post-' + post.slug,
        this.currentLocale,
        newOrder
      );

      const createdPage = await this.websiteService.getPageById(pageId);
      if (createdPage) {
        await this.websiteService.updatePage({
          ...createdPage,
          published: true,
          isExternalLink: true,
          externalUrl: '/' + await this.getPostUrlPrefix(post) + '/' + post.slug,
          openInNewTab: false,
          updatedAt: new Date()
        });
      }

      this.showAddPostForm = false;
      await this.loadMenuStructure();
      this.notificationService.show({
        message: `Post "${post.title}" toegevoegd aan menu`,
        type: 'success'
      });
      this.menuChanged.emit();
    } catch (error) {
      console.error('Error adding post to menu:', error);
      this.notificationService.show({
        message: 'Post toevoegen mislukt',
        type: 'error'
      });
    }
  }

  async loadAvailableProjects() {
    try {
      const projects = await firstValueFrom(this.projectService.getProjects());
      this.availableProjects = projects.filter(p => p.slug && (!p.domains?.length || p.domains.includes(this.domain)));
    } catch (error) {
      console.error('Error loading projects:', error);
      this.availableProjects = [];
    }
  }

  async addProjectToMenu(project: Project) {
    if (!project.slug) return;

    try {
      const allPages = this.getAllPagesFromTree(this.rootPages);
      const maxOrder = allPages.length > 0
        ? Math.max(...allPages.map(p => p.order || 0))
        : -1;
      const newOrder = maxOrder + 1;

      const title = project.title || project.title_nl || 'Project';

      const pageId = await this.websiteService.createPage(
        this.domain,
        title,
        'project-' + project.slug,
        this.currentLocale,
        newOrder
      );

      const createdPage = await this.websiteService.getPageById(pageId);
      if (createdPage) {
        await this.websiteService.updatePage({
          ...createdPage,
          published: true,
          isExternalLink: true,
          externalUrl: '/projecten/' + project.slug,
          openInNewTab: false,
          updatedAt: new Date()
        });
      }

      this.showAddProjectForm = false;
      await this.loadMenuStructure();
      this.notificationService.show({
        message: `Project "${title}" toegevoegd aan menu`,
        type: 'success'
      });
      this.menuChanged.emit();
    } catch (error) {
      console.error('Error adding project to menu:', error);
      this.notificationService.show({
        message: 'Project toevoegen mislukt',
        type: 'error'
      });
    }
  }

  async updateMenuDisplayType(page: PageWithChildren, displayType: string) {
    try {
      const fullPage = await this.websiteService.getPageById(page.id);
      if (!fullPage) return;

      await this.websiteService.updatePage({
        ...fullPage,
        menuDisplayType: displayType as any,
        updatedAt: new Date()
      });

      await this.loadMenuStructure();
      this.notificationService.show({
        message: 'Menu weergave bijgewerkt',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating menu display type:', error);
      this.notificationService.show({
        message: 'Weergave bijwerken mislukt',
        type: 'error'
      });
    }
  }

  async saveMenuHighlight(page: PageWithChildren) {
    try {
      const fullPage = await this.websiteService.getPageById(page.id);
      if (!fullPage) return;

      await this.websiteService.updatePage({
        ...fullPage,
        menuHighlight: page.menuHighlight || {},
        updatedAt: new Date()
      });

      this.notificationService.show({
        message: 'Highlight opgeslagen',
        type: 'success'
      });
    } catch (error) {
      console.error('Error saving menu highlight:', error);
      this.notificationService.show({
        message: 'Highlight opslaan mislukt',
        type: 'error'
      });
    }
  }

  initMenuHighlight(page: PageWithChildren) {
    if (!page.menuHighlight) {
      page.menuHighlight = { image: '', title: '', description: '', ctaText: '', ctaUrl: '' };
    }
  }

  openMediaSelectorForHighlight(page: PageWithChildren) {
    this.mediaSelectorTargetPage = page;
    this.showMediaSelectorModal = true;
  }

  onHighlightMediaSelected(url: string) {
    if (this.mediaSelectorTargetPage) {
      this.initMenuHighlight(this.mediaSelectorTargetPage);
      this.mediaSelectorTargetPage.menuHighlight!.image = url;
    }
    this.closeMediaSelector();
  }

  closeMediaSelector() {
    this.showMediaSelectorModal = false;
    this.mediaSelectorTargetPage = null;
  }

  removeHighlightImage(page: PageWithChildren) {
    if (page.menuHighlight) {
      page.menuHighlight.image = '';
    }
  }

  async toggleHomepage(page: PageWithChildren) {
    try {
      if (page.isHomepage === true) {
        // Unset homepage
        await this.websiteService.unsetHomepage(page.id);
        this.notificationService.show({
          message: 'Homepage status verwijderd',
          type: 'success'
        });
      } else {
        // Set as homepage (this will automatically unset other homepages)
        await this.websiteService.setHomepage(page.id, this.domain, this.currentLocale);
        this.notificationService.show({
          message: 'Pagina ingesteld als homepage',
          type: 'success'
        });
      }

      // Reload menu structure
      await this.loadMenuStructure();
    } catch (error) {
      console.error('Error toggling homepage status:', error);
      this.notificationService.show({
        message: 'Homepage status bijwerken mislukt',
        type: 'error'
      });
    }
  }
} 